import type { ImportedPieceView } from '@obraplan/core/application/contracts/takeoff';
import type {
  ImportedPieceKind,
  ImportedPieceStatus,
} from '@obraplan/core/domain/takeoff/imported-piece';

/**
 * As peças da planilha na prancha (Fase 28, etapa 3): qual ferramenta mostra cada tipo, e a cor da
 * execução. Num arquivo à parte do componente para a recarga a quente do desenvolvimento.
 */

/** A ferramenta da prancha em que cada tipo de peça aparece. */
const MODO_DO_TIPO: Readonly<Record<ImportedPieceKind, string | undefined>> = {
  ESTACA: 'ESTACA',
  BLOCO: 'BLOCO',
  BALDRAME: 'BALDRAME',
  ARRANQUE: 'ARRANQUE',
  MURO: 'MURO',
  PILAR_CONTENCAO: 'PILAR_CONTENCAO',
  OUTRA: undefined,
};

/** O tipo de peça importada que a ferramenta escolhida mostra, se mostra algum. */
export function tipoDaFerramenta(modo: string): ImportedPieceKind | undefined {
  const par = Object.entries(MODO_DO_TIPO).find(([, alvo]) => alvo === modo);
  return par === undefined ? undefined : (par[0] as ImportedPieceKind);
}

export const COR_DA_EXECUCAO: Readonly<Record<ImportedPieceStatus, string>> = {
  NAO_INICIADA: '#64748b',
  EM_EXECUCAO: '#d97706',
  EXECUTADA: '#16a34a',
};

/** O texto da legenda para cada cor da execução (RN-430). */
export const LEGENDA_DA_EXECUCAO: readonly { readonly cor: string; readonly texto: string }[] = [
  { cor: COR_DA_EXECUCAO.NAO_INICIADA, texto: 'Peça da planilha não iniciada' },
  { cor: COR_DA_EXECUCAO.EM_EXECUCAO, texto: 'em execução' },
  { cor: COR_DA_EXECUCAO.EXECUTADA, texto: 'executada' },
];

/**
 * A próxima peça da fila depois de posicionar uma (RN-429): a seguinte sem lugar, do mesmo tipo,
 * na ordem do nome — e, chegando ao fim, a primeira que ainda falta. É o "faltam N" da estaca
 * para todas as peças: com 62 blocos, voltar à lista a cada um seria clicar o dobro.
 *
 * `posicionada` sai da conta mesmo que a lista ainda não saiba: a releitura chega depois do
 * clique. Sem nenhuma outra sem lugar, a fila acabou.
 */
export function proximaDaFila(
  pecas: readonly ImportedPieceView[],
  tipo: ImportedPieceKind,
  posicionada: string,
): string | undefined {
  const faltam = pecas.filter(
    (peca) => peca.kind === tipo && peca.position === undefined && peca.name !== posicionada,
  );
  if (faltam.length === 0) return undefined;
  const depois = faltam.find(
    (peca) => peca.name.localeCompare(posicionada, 'pt-BR', { numeric: true }) > 0,
  );
  return (depois ?? faltam[0])?.name;
}
